import { createContext, FC, useCallback, useEffect, useRef, useState } from 'react'
import SockJS from 'sockjs-client'
import { Client, IStompSocket, messageCallbackType, StompHeaders } from '@stomp/stompjs'

import AuthService from '@services/AuthService'

import { useAuth } from '@hooks'

import { useWakeLock } from '../../hooks/useWakeLock'
import {
  StompSessionProviderContextType,
  StompSessionProviderPropsType,
  StompSessionSubscriptionType,
} from '../../types'

export const StompContext = createContext<StompSessionProviderContextType | undefined>(undefined)

// TODO На текущий момент ws локально не работают
const isDev = false
// const isDev = process.env.NODE_ENV === 'development'

export const StompSessionProvider: FC<StompSessionProviderPropsType> = isDev
  ? ({ children }) => <>{children}</>
  : ({ url, children, stompClientOptions, ...stompOptions }) => {
      if (stompClientOptions) {
        stompOptions = stompClientOptions
      }
      const [client, setClient] = useState<Client | undefined>(undefined)
      const subscriptionRequests = useRef(new Map())
      const staleListenersRef = useRef<Set<(ids: Set<string>) => void>>(new Set())
      const { username } = useAuth()

      useWakeLock(!!username)

      const subscribeToStale = useCallback((fn: (ids: Set<string>) => void) => {
        staleListenersRef.current.add(fn)

        const existingStaleIds = new Set<string>()
        subscriptionRequests.current.forEach((sub, id) => {
          if (sub.needsResync) existingStaleIds.add(id)
        })
        if (existingStaleIds.size > 0) fn(new Set(existingStaleIds))

        return () => staleListenersRef.current.delete(fn)
      }, [])

      useEffect(() => {
        const _client = new Client(stompOptions)

        const handleVisibility = () => {
          if (_client && document.visibilityState === 'visible' && !_client.connected) {
            _client.activate()
          }
        }

        if (username) {
          stompOptions.connectHeaders = {
            Authorization: AuthService.getToken() || '',
            token: AuthService.getToken() || '',
          }
          stompOptions.disconnectHeaders = {
            Authorization: AuthService.getToken() || '',
            token: AuthService.getToken() || '',
          }

          if (!stompOptions.brokerURL && !stompOptions.webSocketFactory) {
            _client.webSocketFactory = function () {
              const parsedUrl = new URL(url, window?.location?.href)
              if (parsedUrl.protocol === 'http:' || parsedUrl.protocol === 'https:') {
                return new SockJS(url) as IStompSocket
              } else if (parsedUrl.protocol === 'ws:' || parsedUrl.protocol === 'wss:') {
                return new WebSocket(url) as IStompSocket
              } else throw new Error('Protocol not supported')
            }
          }
          _client.onConnect = function (frame) {
            if (stompOptions.onConnect) {
              stompOptions.onConnect(frame)
            }

            subscriptionRequests.current.forEach(value => {
              value.needsResync = false
              value.subscription = _client.subscribe(
                value.destination,
                value.callback,
                value.headers
              )
            })

            setClient(_client)
          }

          _client.onWebSocketClose = event => {
            // Код 1000 - нормальное закрытие (deactivate).
            if (event.code !== 1000) {
              const staleIds = new Set<string>()

              subscriptionRequests.current.forEach((sub, id) => {
                sub.needsResync = true
                staleIds.add(id)
              })

              staleListenersRef.current.forEach(fn => fn(new Set(staleIds)))
            }

            if (stompOptions.onWebSocketClose) stompOptions.onWebSocketClose(event)
            setClient(undefined)
          }

          _client.onStompError = frame => {
            const staleIds = new Set<string>()

            subscriptionRequests.current.forEach((sub, id) => {
              sub.needsResync = true
              staleIds.add(id)
            })

            staleListenersRef.current.forEach(fn => fn(new Set(staleIds)))

            if (stompOptions.onStompError) stompOptions.onStompError(frame)
            setClient(undefined)
          }

          document.addEventListener('visibilitychange', handleVisibility)
          _client.activate()
        } else {
          _client?.deactivate()
          setClient(undefined)
        }

        return () => {
          document.removeEventListener('visibilitychange', handleVisibility)
          _client?.deactivate()
        }
      }, [username, ...Object.values(stompOptions)])

      const subscribe = (
        destination: string,
        callback: messageCallbackType,
        headers: StompHeaders = {}
      ) => {
        const subscriptionId = Math.random().toString(36).substr(2, 9)

        const wrappedCallback: messageCallbackType = message => {
          const sub = subscriptionRequests.current.get(subscriptionId)

          if (sub) {
            sub.lastActive = Date.now()
          }

          callback(message)
        }

        const subscriptionRequest: StompSessionSubscriptionType = {
          destination,
          callback: wrappedCallback,
          headers,
          lastActive: Date.now(),
          needsResync: false,
        }

        subscriptionRequests.current.set(subscriptionId, subscriptionRequest)

        if (client && client.connected) {
          subscriptionRequest.subscription = client.subscribe(destination, callback, headers)
        }

        return {
          id: subscriptionId,
          unsubscribe: () => {
            const data = subscriptionRequests.current.get(subscriptionId)
            if (data?.subscription) {
              data.subscription.unsubscribe()
            }
            subscriptionRequests.current.delete(subscriptionId)
          },
        }
      }

      return (
        <StompContext.Provider
          value={{
            client,
            subscribe,
            subscribeToStale,
          }}
        >
          {children}
        </StompContext.Provider>
      )
    }
